import {
  ApiPagination, BaseResponse, Filters, RequestPagination,
} from '@/types';
import { ReportOrderTypes } from '@/enums';

export namespace Reports {
  export enum Pages {
    REPORTS = 'reports',
    REPORT_ORDERS_DETAIL = 'reports/orders',
    REPORT_SALES_DETAIL = 'reports/sales',
    REPORTS_MUTUAL_DETAIL = 'reports/mutual',
    REPORTS_REPAIRS_DETAIL = 'reports/repairs',
    REPORTS_SERVICE_DETAIL = 'reports/service',

    REPORT_ORDERS_ALERT = 'report_orders',
    REPORT_SERVICE_ALERT = 'report_service_result',
    REPORT_REPAIRS_ALERT = 'report_period_repairs',
    REPORT_SALES_ALERT = 'report_sales',
    REPORT_MUTUALS_ALERT = 'report_invoices',
  }

  export interface ProductDetails {
    id: number;
    nomenclature: string;
    product_code?: number | string;
  }

  export interface Product {
    id: number;
    full_price: number | null;
    catalogue_price: string | number | null;
    price_set: string | number | null;
    product: ProductDetails;
    quantity: string | number | null;
    serial_number: string | number | null;
    name_organization?: string
  }

  export interface Report {
    product_code: string,
    product_nomenclature: string,
    order_external_number: string,
    order_created_at: string,

    total_price?: string, // shipped/not_shipped/canceled

    warehouse?: string, // shipped/not_shipped/ready_shipped
    price?: string, // shipped/not_shipped/ready_shipped
    delivery_address?: string, // shipped/not_shipped/ready_shipped
    delivery_type?: string, // shipped/not_shipped/ready_shipped
    contract?: string, // shipped/not_shipped/ready_shipped

    reserve_quantity?: number, // not_shipped/ready_shipped

    volume?: string, // not_shipped/shipped
    weight?: string, // not_shipped/shipped

    remnant_quantity?: number, // not_shipped

    canceled_quantity?: number, // canceled
    canceled_at?: number, // canceled
    cancel_reason?: number, // canceled

    number_realization?: string, // shipped
    shipment_at?: string, // shipped
    shipment_quantity?: number, // shipped

    quantity?: number, // ready_shipped
    reserve_at?: string, // ready_shipped
    reserve_end_at?: string, // ready_shipped
    desired_shipment_date?: string, // ready_shipped
    order_status?: string, // ready_shipped
    name_organization?: string, // ready_shipped
    order_id?: number, // ready_shipped
    external_index?: number, // ready_shipped
    can_be_shipped?: boolean, // ready_shipped
    id?: number; // ready_shipped
    selected?: boolean; // для ready_shipped добавляется на фронте
  }

  export interface SalesPlan {
    category_id: number | null,
    category: string | null,
    price_type: string | null,
    plan: string | null,
    fact: string | null,
    delta: string | null,
    percent: string | null,
    is_done: boolean,
  }

  export interface Repair {
    id: number | null;
    id_number: string | null;
    created_at: string | null;
    end_at: string | null;
    organization_name: string | null;
    nomenclature: string | null;
    serial_number: string | null;
    repair_type: string | null;
    repair_price_rub: string | null;
    zip_price_rub: string | null;
    total_price_rub: string | null;
    zip_products_count: number | null;
    zip_products: Product[];
  }

  export interface ServiceActivity {
    id: number | null;
    organization: string | null;
    year: number | null;
    quarter: number | null;
    repair_period: string | null;
    repair_period_ball: string | null;
    kkt_engineer: string | null;
    its_subscription: string | null;
    its_subscription_ball: string | null;
    number_accredited_product_groups: string | null;
    number_valid_certificates: string | null;
    availability_product_groups_with_certificates: string | null;
    availability_product_groups_with_certificates_ball: string | null;
    platform_2_5: string | null;
    happy_call: string | null;
    happy_call_ball: string | null;
    component_repair: string | null;
    component_repair_ball: string | null;
    competence: string | null;
    competence_ball: string | null;
    evaluation_stp_atol: string | null;
    evaluation_stp_atol_ball: string | null;
    final_score: string | null;
    grade: string | null;
  }

  export interface Mutual {
    id: number;
    order_external_number: string;
    name_organization: string;
    external_organization: string;
    contract: string;
    planned_repayment_at: string;
    total_price: string;
    shipment_amount: string;
    total_payment: string;
    amount_of_debt: string;
    number_days_delay: number;
  }

  export interface RepairsDate {
    [key: string]: number[];
  }
  export interface RepairsPayload {
    [key: string]: unknown;
    year: string;
    month: number;
    organization_id?: number
  }

  export interface Warehouse {
    id: number;
    external_id: string;
    name: string;
    address: string;
    is_main: boolean;
  }

  export interface DeliveryType {
    id: number;
    name: string;
    slug: string;
  }

  export interface DeliveryAddress {
    id: number;
    address: string;
    city: string;
    telephone: string;
    contact_person: string;
    is_veryfied: boolean;
    is_main: boolean;
  }

  export interface Organization {
    id: number
    name_organization: string
    is_deferment_payment: boolean
    is_insured: boolean
  }

  export interface Contract {
    id: number
    name_contract: string
  }

  export interface ExternalOrganization {
    external_organization: string
  }

  export interface Filter {
    contracts: Reports.Contract[],
    organizations: Reports.Organization[],
    external_organizations: Reports.ExternalOrganization[],
  }

  export interface CancelReason {
    id: number;
    name: string;
  }

  export interface OrderFilterContract {
    id: number;
    name: string;
  }

  export interface OrdersReportsFilters {
    [key: string]: unknown,
    date: string[] | null;
    organization_ids: number[] | null;
    delivery_types: number[] | null;
    delivery_address_ids?: number[] | null;
    contracts: number[] | null
    warehouses_ids: number[] | null;
    cancel_reasons_ids: number[] | null;
    order_numbers: number[] | null;
    type: ReportOrderTypes;
  }

  export interface OrdersReportsTotalInfo {
    dates: string | null,
    orders: number | null,
    products_quantity: number | null,
    price: number | null,
    volume: number | null,
    weight: number | null,
  }

  export interface MutualsDebtInfo {
    dates: string;
    total_price: number | null;
    total_shipment_amount: number | null;
    total_amount: number | null;
    amount_of_debt: number | null;
  }

  export interface MutualsReportsFilters extends Filters {
    [key: string]: unknown,
    date: string[] | null;
    organization_ids: number[] | null;
    external_organizations: string[] | null;
    contracts: string[] | null;
    is_overdue_debt: boolean
  }

  export interface SalesReportsFilters extends Filters {
    quarter: number | null
    year: number | null
  }
  export interface ServiceReportsFilters extends Filters {
    quarter: number | null
    year: number | null
    organization_ids: number[];
  }
  export interface TabsMapState {
    [key: number]: {
      type: string,
      sortBy: string[]
    }
  }

  export interface Permission {
    id: number;
    slug: string;
    name: string;
    is_allowed: boolean;
  }

  export interface ReportsDatesGetter {
    month: number,
    year: string,
  }

  export interface ReportOrderFilters {
    organizations: Organization[],
    delivery_types: DeliveryType[],
    warehouses: Warehouse[],
    delivery_addresses: DeliveryAddress[],
    cancel_reasons: CancelReason[],
    contracts: OrderFilterContract[],
    order_numbers: string[],
  }

  export interface RepairsDates extends BaseResponse {
    data: {
      dates: RepairsDate
    }
  }

  export interface Permissions extends BaseResponse {
    data: Permission[]
  }

  export interface Orders extends ApiPagination {
    data: {
      items: Report[],
      filters: ReportOrderFilters,
      total: OrdersReportsTotalInfo
    }
  }

  export interface Repairs extends BaseResponse {
    data: {
      items: Repair[];
    };
  }

  export interface ServiceActivities extends BaseResponse, ApiPagination {
    data: {
      filters: {
        years: number[],
        organizations: Organization,
      },
      default_quarter: number,
      default_year: number,
      items: ServiceActivity[];
    };
  }

  export interface Mutuals extends BaseResponse, ApiPagination {
    data: {
      file: string,
      filters: Filter,
      total: MutualsDebtInfo,
      items: Mutual[];
    };
  }

  export interface PlanFactSales extends ApiPagination {
    data: {
      total_percent: number,
      filters: {
        years: number[],
        quarter: number
      }
      items: SalesPlan[];
    };
  }

  export type PlanFactRequest = {
    filters: SalesReportsFilters,
    filters_exist: boolean,
    pagination: RequestPagination,
  }

  export type OrdersRequest = {
    filters: OrdersReportsFilters,
    filters_exist: boolean,
    total_exist: boolean,
    pagination: RequestPagination,
  }

  export type MutualRequest = {
    filters: MutualsReportsFilters,
    filters_exist: boolean,
    pagination: RequestPagination,
  }
}
